import { useEffect } from "react";

import { useBoxStore } from "@shared/states/boxStore";
import { getBoxBaseUrls, type BoxBaseUrls } from "@shared/utils/box";

/**
 * Query param carrying the proxy flag into the standalone `/box` pages, which
 * render unauthenticated and so have no OEM to read the flag off.
 */
export const BOX_PROXY_QUERY_PARAM = "boxProxy";

/**
 * Publishes the signed-in OEM's Box base URLs. Called once from the app layout,
 * which already holds `currentUser` via `useAuth` — no query of its own.
 */
export const useSyncBoxBaseUrls = (
  boxProxyEnabled: boolean | null | undefined,
): void => {
  const setBoxBaseUrls = useBoxStore((state) => state.setBoxBaseUrls);

  useEffect(() => {
    setBoxBaseUrls(getBoxBaseUrls(boxProxyEnabled));
  }, [boxProxyEnabled, setBoxBaseUrls]);
};

/**
 * Box base URLs for the signed-in OEM, or `undefined` when the OEM is not
 * proxied — which every consumer can pass straight through, since Box's own
 * hosts are the default in that case.
 */
const useBoxBaseUrls = (): { boxBaseUrls: BoxBaseUrls | undefined } => ({
  boxBaseUrls: useBoxStore((state) => state.boxBaseUrls),
});

/**
 * Box base URLs for unauthenticated entry points, where the flag has to arrive
 * in the URL. Deliberately not reading `currentUser`: the `/box` routes sit
 * outside the auth guard, and firing that query there would 401.
 */
export const getBoxBaseUrlsFromSearch = (
  search: string,
): BoxBaseUrls | undefined => {
  const value = new URLSearchParams(search).get(BOX_PROXY_QUERY_PARAM);
  return getBoxBaseUrls(value === "true" || value === "1");
};

export default useBoxBaseUrls;
